import { useParams, Link } from 'react-router-dom';
import { useState, useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import MovieCard from '@/components/base/MovieCard';
import { movies } from '@/mocks/movies';
import { categories, categoryNames } from '@/mocks/categories';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [sortBy, setSortBy] = useState<'rating' | 'year' | 'default'>('default');
  const [showFilters, setShowFilters] = useState(false);

  const currentCategory = categories.find((c) => c.slug === slug) || categories[0];
  const currentName = categoryNames[slug || 'all'] || '全部电影';

  const filteredMovies = useMemo(() => {
    let list = slug && slug !== 'all' ? movies.filter((m) => m.category === slug) : [...movies];

    if (sortBy === 'rating') {
      list = [...list].sort((a, b) => b.rating - a.rating);
    } else if (sortBy === 'year') {
      list = [...list].sort((a, b) => b.year - a.year);
    }

    return list;
  }, [slug, sortBy]);

  return (
    <>
      <ScrollToTop />
      <div className="min-h-screen bg-background-50">
        <Navbar />

        <main className="pt-20">
          <div className="px-4 md:px-6 lg:px-8 py-6">
            <div className="max-w-7xl mx-auto">
              <Breadcrumb
                items={[
                  { label: '首页', href: '/' },
                  { label: currentName },
                ]}
              />
            </div>
          </div>

          {/* Category Header */}
          <section className="px-4 md:px-6 lg:px-8 pb-6">
            <div className="max-w-7xl mx-auto">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-primary-500/20">
                  <i className={`${currentCategory.icon} text-primary-500 text-xl`}></i>
                </div>
                <h1 className="text-2xl md:text-3xl font-bold text-foreground-50 font-heading">
                  {currentName}
                </h1>
                <span className="px-2 py-0.5 rounded-md bg-background-100 text-foreground-400 text-xs">
                  {filteredMovies.length} 部
                </span>
              </div>

              {/* Sub Category Tabs */}
              <div className="flex items-center gap-1 flex-wrap mb-6">
                {categories.map((cat) => (
                  <Link
                    key={cat.slug}
                    to={cat.slug === 'all' ? '/' : `/category/${cat.slug}`}
                    className={`px-3 py-1.5 rounded-full text-sm font-medium transition-all duration-200 whitespace-nowrap ${
                      (cat.slug === 'all' && (!slug || slug === 'all')) || cat.slug === slug
                        ? 'bg-primary-500 text-background-50'
                        : 'bg-background-100 text-foreground-300 hover:bg-background-200 hover:text-foreground-50'
                    }`}
                  >
                    {cat.name}
                  </Link>
                ))}
              </div>

              {/* Sort */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setSortBy('default')}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                      sortBy === 'default'
                        ? 'bg-background-100 text-foreground-50'
                        : 'text-foreground-400 hover:text-foreground-200'
                    }`}
                  >
                    默认排序
                  </button>
                  <button
                    onClick={() => setSortBy('rating')}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                      sortBy === 'rating'
                        ? 'bg-background-100 text-foreground-50'
                        : 'text-foreground-400 hover:text-foreground-200'
                    }`}
                  >
                    <i className="ri-star-line mr-1"></i>评分最高
                  </button>
                  <button
                    onClick={() => setSortBy('year')}
                    className={`px-3 py-1.5 rounded-md text-xs font-medium transition-colors cursor-pointer whitespace-nowrap ${
                      sortBy === 'year'
                        ? 'bg-background-100 text-foreground-50'
                        : 'text-foreground-400 hover:text-foreground-200'
                    }`}
                  >
                    <i className="ri-calendar-line mr-1"></i>最新上映
                  </button>
                </div>

                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="md:hidden px-3 py-1.5 rounded-md text-xs font-medium text-foreground-400 hover:text-foreground-200 hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-filter-3-line mr-1"></i>筛选
                </button>
              </div>
            </div>
          </section>

          {/* Movie Grid */}
          <section className="px-4 md:px-6 lg:px-8 py-6">
            <div className="max-w-7xl mx-auto">
              {filteredMovies.length > 0 ? (
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-3 md:gap-4">
                  {filteredMovies.map((movie) => (
                    <MovieCard key={movie.id} movie={movie} />
                  ))}
                </div>
              ) : (
                <div className="text-center py-20">
                  <div className="w-16 h-16 flex items-center justify-center rounded-full bg-background-100 mx-auto mb-4">
                    <i className="ri-movie-line text-foreground-400 text-2xl"></i>
                  </div>
                  <p className="text-foreground-400">暂无该分类的电影资源</p>
                  <Link
                    to="/"
                    className="inline-flex items-center gap-1 mt-4 text-primary-500 hover:text-primary-400 text-sm transition-colors"
                  >
                    <i className="ri-arrow-left-line"></i> 返回首页
                  </Link>
                </div>
              )}
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}